"use client";

import Link from "next/link";
import Image from "next/image";
import { format } from "date-fns";
import { getFeaturedImage } from "../../utils/imageUtils";
import { useRouter } from "next/navigation";

export default function StandardPostGrid({ posts, postType }) {
  const router = useRouter();

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const navigateTo = (post) => {
    if (!post?.slug) return;
    router.push(`/${postType}/${post.slug}`);
  };

  const stop = (e) => e.stopPropagation();

  const renderPostCard = (post, index) => {
    switch (postType) {
      case "event":
        return (
          <div
            onClick={() => navigateTo(post)}
            className="bg-white rounded-sm overflow-hidden border border-gray-100 relative cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
            role="button"
            tabIndex={0}
          >
            <div className="w-full h-64 relative">
              <Image
                src={getFeaturedImage(post, "medium")}
                alt={post.title}
                width={400}
                height={300}
                className="w-full h-full object-cover"
                priority={index < 3}
              />
              <div className="absolute bottom-0 left-0 bg-gray-100 text-center p-2 w-16">
                <div className="font-bold text-lg">
                  {format(
                    new Date(
                      post.eventDate || post.publishDate || post.createdAt
                    ),
                    "dd"
                  )}
                </div>
                <div className="uppercase text-sm">
                  {format(
                    new Date(
                      post.eventDate || post.publishDate || post.createdAt
                    ),
                    "MMM"
                  )}
                </div>
                <div className="text-sm">
                  {format(
                    new Date(
                      post.eventDate || post.publishDate || post.createdAt
                    ),
                    "yyyy"
                  )}
                </div>
              </div>
            </div>
            <div className="p-4">
              <h3 className="text-lg font-semibold line-clamp-3 group-hover:text-primary transition-colors">
                {post.title}
              </h3>
            </div>
            <div className="flex justify-between p-2 border-t">
              <div className="flex space-x-2">
                <button onClick={stop} className="text-blue-500 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path>
                  </svg>
                </button>
                <button onClick={stop} className="text-blue-400 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path>
                  </svg>
                </button>
                <button onClick={stop} className="text-blue-600 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"></path>
                  </svg>
                </button>
              </div>
              <Link href={`/${postType}/${post.slug}`} onClick={stop} className="text-pink-500 font-medium hover:underline flex items-center">
                Read More
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="ml-1"
                >
                  <path d="M5 12h14"></path>
                  <path d="M12 5l7 7-7 7"></path>
                </svg>
              </Link>
            </div>
          </div>
        );

      case "press-release":
        return (
          <div
            onClick={() => navigateTo(post)}
            className="bg-white rounded-sm overflow-hidden border border-gray-100 flex flex-col cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
            role="button"
            tabIndex={0}
          >
            <div className="w-full h-48 relative">
              <Image
                src={getFeaturedImage(post, "medium")}
                alt={post.title}
                width={400}
                height={225}
                className="w-full h-full object-cover"
                priority={index < 3}
              />
              <div className="absolute top-2 right-2">
                <button onClick={stop} className="text-pink-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 flex-grow">
              <div className="text-sm text-gray-500 mb-2">
                {formatDate(post.publishDate || post.createdAt)}
              </div>
              <h3 className="text-lg font-semibold line-clamp-3 group-hover:text-primary transition-colors">
                {post.title}
              </h3>
            </div>
            <div className="mt-auto flex justify-between p-2 border-t">
              <div className="flex space-x-2">
                <button onClick={stop} className="text-blue-500 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-400 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-600 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"></path></svg>
                </button>
              </div>
              <div className="flex items-center">
                <Link href={`/${postType}/${post.slug}`} onClick={stop} className="text-pink-500 font-medium hover:underline flex items-center">
                  Read More
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
                </Link>
              </div>
            </div>
          </div>
        );

      case "blog":
        return (
          <div
            onClick={() => navigateTo(post)}
            className="bg-white rounded-sm overflow-hidden border border-gray-100 flex flex-col cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
            role="button"
            tabIndex={0}
          >
            <div className="w-full h-48 relative">
              <Image
                src={getFeaturedImage(post, "medium")}
                alt={post.title}
                width={400}
                height={225}
                className="w-full h-full object-cover"
                priority={index < 3}
              />
              {post.category && (
                <div className="absolute top-4 left-0">
                  <span className="bg-orange-500 text-white px-3 py-1 uppercase text-xs font-medium">
                    {post.category}
                  </span>
                </div>
              )}
              <div className="absolute top-2 right-2">
                <button onClick={stop} className="text-pink-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 flex-grow">
              <h3 className="text-lg font-semibold line-clamp-2 group-hover:text-primary transition-colors mb-2">
                {post.title}
              </h3>
              <div className="text-sm text-gray-500">
                {formatDate(post.publishDate || post.createdAt)}
              </div>
            </div>
            <div className="mt-auto flex justify-between p-2 border-t">
              <div className="flex space-x-2">
                <button onClick={stop} className="text-blue-500 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-400 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-600 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"></path></svg>
                </button>
              </div>
              <div className="flex items-center">
                <Link href={`/${postType}/${post.slug}`} onClick={stop} className="text-pink-500 font-medium hover:underline flex items-center">
                  Read More
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
                </Link>
              </div>
            </div>
          </div>
        );

      case "video":
        return (
          <div
            onClick={() => navigateTo(post)}
            className="bg-white rounded-sm overflow-hidden border border-gray-100 flex flex-col cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
            role="button"
            tabIndex={0}
          >
            <div className="w-full h-48 relative">
              <div className="relative w-full h-full">
                <Image
                  src={getFeaturedImage(post, "medium")}
                  alt={post.title}
                  width={400}
                  height={225}
                  className="w-full h-full object-cover"
                  priority={index < 3}
                />
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                  <div className="bg-red-600 rounded-full w-12 h-12 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                  </div>
                </div>
                {post.duration && (
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-0.5 text-xs rounded">
                    {post.duration}
                  </div>
                )}
              </div>
              <div className="absolute top-2 right-2">
                <button onClick={stop} className="text-pink-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 flex-grow">
              <h3 className="text-lg font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                {post.title}
              </h3>
              <div className="text-sm text-gray-500 mt-1">
                {formatDate(post.publishDate || post.createdAt)}
              </div>
            </div>
            <div className="mt-auto flex justify-between p-2 border-t">
              <div className="flex space-x-2">
                <button onClick={stop} className="text-blue-500 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-400 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-600 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"></path></svg>
                </button>
              </div>
              <div className="flex items-center">
                <Link href={`/${postType}/${post.slug}`} onClick={stop} className="text-pink-500 font-medium hover:underline flex items-center">
                  Read More
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
                </Link>
              </div>
            </div>
          </div>
        );

      case "gallery":
        return (
          <div
            onClick={() => navigateTo(post)}
            className="bg-white rounded-sm overflow-hidden border border-gray-100 flex flex-col cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
            role="button"
            tabIndex={0}
          >
            <div className="relative">
              <div className="w-full h-60">
                <Image
                  src={getFeaturedImage(post, "medium")}
                  alt={post.title}
                  width={400}
                  height={300}
                  className="w-full h-full object-cover"
                  priority={index < 3}
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 p-3">
                <p className="text-white font-medium">{post.title}</p>
                <div className="text-white text-sm opacity-90">
                  {formatDate(post.publishDate || post.createdAt)}
                </div>
              </div>
            </div>
            <div className="p-2 flex justify-between items-center">
              <div className="flex space-x-2">
                <button onClick={stop} className="text-blue-500 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-400 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-600 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"></path></svg>
                </button>
              </div>
              <Link href={`/${postType}/${post.slug}`} onClick={stop} className="text-pink-500 font-medium hover:underline flex items-center">
                View Gallery
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
              </Link>
            </div>
          </div>
        );

      case "media-coverage":
        return (
          <div
            onClick={() => navigateTo(post)}
            className="bg-white rounded-sm overflow-hidden border border-gray-100 flex flex-col cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
            role="button"
            tabIndex={0}
          >
            <div className="w-full h-48 relative">
              <Image
                src={getFeaturedImage(post, "medium")}
                alt={post.title}
                width={400}
                height={225}
                className="w-full h-full object-cover"
                priority={index < 3}
              />
              <div className="absolute top-2 right-2">
                <button onClick={stop} className="text-pink-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 flex-grow">
              <div className="text-sm text-gray-500 mb-2">
                {formatDate(post.publishDate || post.createdAt)}
              </div>
              <h3 className="text-lg font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                {post.title}
              </h3>
            </div>
            <div className="mt-auto flex justify-between p-2 border-t">
              <div className="flex space-x-2">
                <button onClick={stop} className="text-blue-500 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-400 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-600 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"></path></svg>
                </button>
              </div>
              <div className="flex items-center">
                <Link href={`/${postType}/${post.slug}`} onClick={stop} className="text-pink-500 font-medium hover:underline flex items-center">
                  Read More
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
                </Link>
              </div>
            </div>
          </div>
        );

      case "members-list":
        // Get first few member avatars to show as preview (sorted by rank)
        const sortedMembers = post.members
          ? [...post.members].sort((a, b) => (a.rank || 999) - (b.rank || 999))
          : [];
        const memberAvatars = sortedMembers.slice(0, 4);
        const remainingCount =
          sortedMembers.length > 4 ? sortedMembers.length - 4 : 0;

        return (
          <div
            onClick={() => navigateTo(post)}
            className="bg-white rounded-sm overflow-hidden border border-gray-100 flex flex-col cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
            role="button"
            tabIndex={0}
          >
            <div className="w-full h-48 relative bg-gray-100">
              {/* Show member avatars as preview */}
              {memberAvatars.length > 0 ? (
                <div className="grid grid-cols-2 gap-0.5 h-full">
                  {memberAvatars.map((member, avatarIndex) => (
                    <div key={avatarIndex} className="relative">
                      <Image
                        src={member.avatar}
                        alt={member.name}
                        width={200}
                        height={120}
                        className="w-full h-full object-cover"
                        priority={index < 3}
                      />
                      {/* Show "+X more" overlay on last image if there are more members */}
                      {avatarIndex === 3 && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                          <span className="text-white font-semibold text-lg">
                            +{remainingCount} more
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : post.featuredImage ? (
                <Image
                  src={post.featuredImage}
                  alt={post.title || "Members"}
                  width={400}
                  height={225}
                  className="w-full h-full object-cover"
                  priority={index < 3}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-400"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
              )}
            </div>
            <div className="p-4 flex-grow">
              <h3 className="text-lg font-semibold line-clamp-2 group-hover:text-primary transition-colors mb-2">
                {post.title || "Members List"}
              </h3>
              <div className="text-sm text-gray-500 mb-2">
                {post.members
                  ? `${post.members.length} member${
                      post.members.length !== 1 ? "s" : ""
                    }`
                  : "No members"}
              </div>
              <div className="text-sm text-gray-500">
                {formatDate(post.publishDate || post.createdAt)}
              </div>
            </div>
            <div className="mt-auto flex justify-between p-2 border-t">
              <div className="flex space-x-2">
                <button onClick={stop} className="text-blue-500 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-400 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path></svg>
                </button>
                <button onClick={stop} className="text-blue-600 p-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"></path></svg>
                </button>
              </div>
              <div className="flex items-center">
                <Link href={`/${postType}/${post.slug}`} onClick={stop} className="text-pink-500 font-medium hover:underline flex items-center">
                  View Members
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
                </Link>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div
            onClick={() => navigateTo(post)}
            className="bg-white rounded-sm overflow-hidden border border-gray-100 cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
            role="button"
            tabIndex={0}
          >
            <div className="w-full h-48 relative">
              <Image
                src={getFeaturedImage(post, "medium")}
                alt={post.title}
                width={400}
                height={225}
                className="w-full h-full object-cover"
                priority={index < 3}
              />
            </div>

            <div className="p-4">
              <Link href={`/${postType}/${post.slug}`} onClick={stop} className="group">
                <span className="text-lg font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                  {post.title}
                </span>
              </Link>

              <div className="flex items-center text-sm text-gray-500 mt-2">
                <span>
                  {formatDate(
                    post.publishDate || post.eventDate || post.createdAt
                  )}
                </span>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {posts.map((post, index) => (
        <div key={index} className="group block">
          {renderPostCard(post, index)}
        </div>
      ))}

      {posts.length === 0 && (
        <div className="col-span-full text-center py-12">
          <p className="text-gray-500">No posts available at this time.</p>
        </div>
      )}
    </div>
  );
}
